// Runtime values of the rules language and conversion from Estra wire values.
export class RuleError extends Error {}
export const err = (m: string): never => {
  throw new RuleError(m);
};

export class RMap {
  readonly o: Record<string, Value>;
  constructor(o: Record<string, Value>) {
    this.o = o;
  }
  has(k: string) {
    return Object.prototype.hasOwnProperty.call(this.o, k);
  }
  get(k: string): Value {
    return this.has(k) ? this.o[k]! : err(`no key "${k}"`);
  }
}
export class RTimestamp {
  readonly ms: number;
  constructor(ms: number) {
    this.ms = ms;
  }
}
export class RDuration {
  readonly ms: number;
  constructor(ms: number) {
    this.ms = ms;
  }
}
export class RLatLng {
  readonly lat: number;
  readonly lng: number;
  constructor(lat: number, lng: number) {
    this.lat = lat;
    this.lng = lng;
  }
}
export class RPath {
  readonly segs: string[];
  constructor(segs: string[]) {
    this.segs = segs;
  }
  toString() {
    return "/" + this.segs.join("/");
  }
}
export class RSet {
  readonly items: Value[];
  constructor(items: Value[]) {
    this.items = [];
    for (const x of items) if (!this.items.some((y) => eq(x, y))) this.items.push(x);
  }
  has(x: Value) {
    return this.items.some((y) => eq(x, y));
  }
}
export class RMapDiff {
  readonly a: RMap;
  readonly b: RMap;
  constructor(a: RMap, b: RMap) {
    this.a = a;
    this.b = b;
  }
}
/** A namespace or builtin, callable or with members (math, timestamp, duration …). */
export class RBuiltin {
  readonly name: string;
  readonly call?: (args: Value[]) => Value | Promise<Value>;
  readonly members?: Record<string, RBuiltin>;
  constructor(name: string, call?: (args: Value[]) => Value | Promise<Value>, members?: Record<string, RBuiltin>) {
    this.name = name;
    if (call) this.call = call;
    if (members) this.members = members;
  }
}

/** What a list query pins a field to (from its where clauses). */
export interface Constraint {
  in?: Value[];
  lo?: { v: Value; incl: boolean };
  hi?: { v: Value; incl: boolean };
  /** array-contains / array-contains-any values (any-of when more than one). */
  contains?: Value[];
}

/** A value a query check can't know (a field the query doesn't pin, a wildcard id). */
export class RUnknown {
  readonly c: Constraint | undefined;
  constructor(c?: Constraint) {
    this.c = c;
  }
}
export const UNKNOWN = new RUnknown();

/** resource.data during a query check: pinned fields are known, the rest are unknown. */
export class RSymMap {
  readonly known: Record<string, Value>;
  readonly cons: Record<string, Constraint>;
  constructor(known: Record<string, Value>, cons: Record<string, Constraint>) {
    this.known = known;
    this.cons = cons;
  }
  get(k: string): Value {
    if (Object.prototype.hasOwnProperty.call(this.known, k)) return this.known[k]!;
    // A dotted pin ("stats.status" == …) makes resource.data.stats a map with that key known.
    const pre = `${k}.`;
    const subKnown = Object.fromEntries(Object.entries(this.known).filter(([x]) => x.startsWith(pre)).map(([x, v]) => [x.slice(pre.length), v]));
    const subCons = Object.fromEntries(Object.entries(this.cons).filter(([x]) => x.startsWith(pre)).map(([x, v]) => [x.slice(pre.length), v]));
    if (Object.keys(subKnown).length || Object.keys(subCons).length) return new RSymMap(subKnown, subCons);
    return new RUnknown(this.cons[k]);
  }
}

export type Value = null | boolean | number | string | Value[] | RMap | RTimestamp | RDuration | RLatLng | RPath | RSet | RMapDiff | RBuiltin | RUnknown | RSymMap;

export function eq(a: Value, b: Value): boolean {
  if (a === b) return true;
  if (a === null || b === null) return false;
  if (Array.isArray(a) && Array.isArray(b)) return a.length === b.length && a.every((x, i) => eq(x, b[i]!));
  if (a instanceof RMap && b instanceof RMap) {
    const ka = Object.keys(a.o), kb = Object.keys(b.o);
    return ka.length === kb.length && ka.every((k) => b.has(k) && eq(a.o[k]!, b.o[k]!));
  }
  if (a instanceof RTimestamp && b instanceof RTimestamp) return a.ms === b.ms;
  if (a instanceof RDuration && b instanceof RDuration) return a.ms === b.ms;
  if (a instanceof RLatLng && b instanceof RLatLng) return a.lat === b.lat && a.lng === b.lng;
  if (a instanceof RPath && b instanceof RPath) return a.toString() === b.toString();
  if (a instanceof RSet && b instanceof RSet) return a.items.length === b.items.length && a.items.every((x) => b.has(x));
  return false;
}

/** Estra wire value → rules value ({$t} → timestamp, {$geo} → latlng, {$ref} → path). */
export function fromWire(v: unknown): Value {
  if (v === null || v === undefined) return null;
  if (Array.isArray(v)) return v.map(fromWire);
  if (typeof v === "object") {
    const o = v as Record<string, unknown>;
    if (typeof o.$t === "number" && Object.keys(o).length === 1) return new RTimestamp(o.$t);
    if (Array.isArray(o.$geo) && Object.keys(o).length === 1) return new RLatLng(o.$geo[0] as number, o.$geo[1] as number);
    if (typeof o.$ref === "string" && Object.keys(o).length === 1) return docPath(o.$ref);
    return new RMap(Object.fromEntries(Object.entries(o).map(([k, x]) => [k, fromWire(x)])));
  }
  return v as boolean | number | string;
}

export const ROOT = ["databases", "(default)", "documents"];
/** A document path as rules see it: /databases/(default)/documents/<path>. */
export const docPath = (p: string) => new RPath([...ROOT, ...p.split("/").filter(Boolean)]);
/** Back to an Estra document path ("users/u1"); null if outside the documents root. */
export function relPath(p: RPath): string | null {
  const s = p.segs;
  if (s.length < 3 || s[0] !== "databases" || s[2] !== "documents") return null;
  return s.slice(3).join("/");
}

export function typeName(v: Value): string {
  if (v === null) return "null";
  if (typeof v === "boolean") return "bool";
  if (typeof v === "number") return Number.isInteger(v) ? "int" : "float";
  if (typeof v === "string") return "string";
  if (Array.isArray(v)) return "list";
  if (v instanceof RMap) return "map";
  if (v instanceof RTimestamp) return "timestamp";
  if (v instanceof RDuration) return "duration";
  if (v instanceof RLatLng) return "latlng";
  if (v instanceof RPath) return "path";
  if (v instanceof RSet) return "set";
  if (v instanceof RMapDiff) return "map_diff";
  if (v instanceof RSymMap) return "map";
  if (v instanceof RUnknown) return "unknown";
  return "function";
}
